// src/cockpit4/components/EffectReviewPanel.tsx
// 效果复盘看板（v6.41）
//   定位：经营中心 → 经营情报区「效果复盘」Tab，把趋势 + 聚合 + 周报做成一屏复盘视图
//   数据源：
//     1) GET /stats?key=xeaos2026&days=14  —— 官网访问统计（趋势折线 + 聚合指标）
//     2) CEORuntime generate_report(weekly) —— AI CEO 自动周报（真数据聚合 + Obsidian 归档）
//   板块：
//     A) 聚合统计  —— 今日/累计/GEO访客/下载/平均停留/跳出率
//     B) 效果趋势  —— 近 14 天访问折线图（访问量 + 独立 IP 双线，SVG 实时绘制）
//     C) 自动周报  —— 一键生成 AI CEO 周报，展示头条/核心指标/建议/风险，并归档到 Obsidian

import { useEffect, useCallback, useState } from "react";
import {
  Globe,
  RefreshCw,
  Loader2,
  TrendingUp,
  Users,
  FileDown,
  Clock,
  Target,
  FileText,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Zap,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import { CEOCommand } from "../../runtime/AgentRuntime/ceo/types";

const STATS_KEY = "xeaos2026";

interface DayStat {
  date: string;
  visits: number;
  uniqueIps: number;
}
interface StatsData {
  totalVisits: number;
  todayVisits: number;
  totalVisitors: number;
  days: DayStat[];
  geoTotalVisitors: number;
  avgDuration: number;
  bounceRate: number;
  downloadCount: number;
}

// ============================================
// 聚合统计卡片（真实 /stats 数据，无占位假数据）
// ============================================

function StatCard({
  label,
  value,
  color,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  color: string;
  icon: typeof Globe;
}) {
  return (
    <div className="px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={10} style={{ color }} />
        <span className="text-[9px] font-mono text-gray-500">{label}</span>
      </div>
      <div className="text-[16px] font-bold leading-none" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

// ============================================
// 主组件
// ============================================

export default function EffectReviewPanel() {
  const [data, setData] = useState<StatsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  // 自动周报状态
  const [reportBusy, setReportBusy] = useState(false);
  const [reportMsg, setReportMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [report, setReport] = useState<any>(null);

  // ===== 加载官网统计 =====
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    setError("");
    try {
      const r = await fetch(`/stats?key=${STATS_KEY}&days=14`).catch(() => null);
      if (!r || !r.ok) {
        setError("官网统计接口不可用（/stats 未响应）");
        setData(null);
        return;
      }
      const d = await r.json();
      if (!d.success) {
        setError(`官网统计接口报错：${d.error || "未知"}`);
        setData(null);
        return;
      }
      setData({
        totalVisits: d.totalVisits ?? 0,
        todayVisits: d.todayVisits ?? 0,
        totalVisitors: d.totalVisitors ?? 0,
        days: Array.isArray(d.days) ? d.days : [],
        geoTotalVisitors: d.geoTotalVisitors ?? 0,
        avgDuration: d.avgDuration ?? 0,
        bounceRate: d.bounceRate ?? 0,
        downloadCount: d.downloadCount ?? 0,
      });
    } catch {
      setError("官网统计加载失败");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ===== 自动周报：调用 CEORuntime 生成 =====
  const generateReport = useCallback(async () => {
    setReportBusy(true);
    setReportMsg(null);
    setReport(null);
    try {
      if (!RuntimeManager.isInitialized() || !RuntimeManager.hasRuntime(RuntimeName.CEO)) {
        setReportMsg({ ok: false, text: "CEORuntime 未注册，无法生成自动周报" });
        return;
      }
      const resp: any = await RuntimeManager.execute(RuntimeName.CEO, CEOCommand.GenerateReport, {
        period: "weekly",
      });
      if (!resp?.success) {
        setReportMsg({ ok: false, text: `周报生成失败：${resp?.error || "未知错误"}` });
        return;
      }
      // 双层解包：resp.data 是 CEOResult，resp.data.data 才是 CEOReport
      const ceoResult = resp.data as any;
      if (!ceoResult?.success || !ceoResult.data) {
        setReportMsg({ ok: false, text: ceoResult?.error || "周报生成结果为空" });
        return;
      }
      const theReport = ceoResult.data;
      setReport(theReport);
      setReportMsg({
        ok: true,
        text: `周报已生成并归档（数据点 ${theReport?.metadata?.dataPoints ?? 0}）`,
      });
    } catch (e: any) {
      setReportMsg({ ok: false, text: `周报生成异常：${e?.message || String(e)}` });
    } finally {
      setReportBusy(false);
    }
  }, []);

  // ===== 格式化 =====
  const fmtDur = (ms: number) => {
    if (!ms) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m${s % 60}s`;
  };

  const fmtNum = (n: number) => {
    if (!n) return "0";
    return n >= 10000 ? `${(n / 10000).toFixed(1)}w` : String(n);
  };

  // ===== 趋势折线图（SVG 双线：访问量 + 独立 IP） =====
  const renderTrend = () => {
    const days = data?.days ?? [];
    if (days.length === 0) {
      return (
        <div className="text-[10px] font-mono text-gray-600 py-6 text-center">
          近 14 天暂无访问数据
        </div>
      );
    }
    const TW = 560;
    const TH = 150;
    const padT = 10;
    const padB = 18;
    const padL = 6;
    const plotH = TH - padT - padB;
    const plotW = TW - padL * 2;
    const maxV = Math.max(1, ...days.map((d) => Math.max(d.visits, d.uniqueIps || 0)));
    const slot = plotW / days.length;
    const px = (i: number) => padL + i * slot + slot / 2;
    const py = (v: number) => padT + plotH - (v / maxV) * plotH;
    const line = (key: "visits" | "uniqueIps", color: string) => {
      const pts = days.map((d, i) => `${px(i)},${py(d[key] ?? 0)}`).join(" L ");
      const area = `${px(0)},${padT + plotH} L ${pts} L ${px(days.length - 1)},${padT + plotH} Z`;
      return (
        <g key={key}>
          {key === "visits" && (
            <path d={area} fill="url(#revArea)" />
          )}
          <path d={`M ${pts}`} fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          {days.map((d, i) => (
            <circle
              key={i}
              cx={px(i)}
              cy={py(d[key] ?? 0)}
              r="2"
              fill="#0B1120"
              stroke={color}
              strokeWidth="1"
            >
              <title>{`${d.date} · ${d[key] ?? 0}${key === "uniqueIps" ? " IP" : " 次"}`}</title>
            </circle>
          ))}
        </g>
      );
    };
    const gridFrac = [0.25, 0.5, 0.75, 1];
    const total14 = days.reduce((s, d) => s + d.visits, 0);
    const first = days[0]?.visits ?? 0;
    const last = days[days.length - 1]?.visits ?? 0;
    const delta = first > 0 ? Math.round(((last - first) / first) * 100) : 0;
    return (
      <div>
        <div className="flex items-center gap-3 mb-1.5 text-[9px] font-mono text-gray-500 flex-wrap">
          <span>
            14 天总访问 <span className="text-gray-200">{total14}</span>
          </span>
          <span>
            日均 <span className="text-gray-200">{days.length ? Math.round(total14 / days.length) : 0}</span>
          </span>
          <span className={delta >= 0 ? "text-emerald-400" : "text-rose-400"}>
            {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%（较 14 天前）
          </span>
          <span className="ml-auto flex items-center gap-3">
            <span className="flex items-center gap-1"><i className="w-2 h-[2px] rounded bg-[#22D3EE] inline-block" />访问量</span>
            <span className="flex items-center gap-1"><i className="w-2 h-[2px] rounded bg-[#A78BFA] inline-block" />独立 IP</span>
          </span>
        </div>
        <svg viewBox={`0 0 ${TW} ${TH}`} className="w-full h-auto block" role="img" aria-label="近14天访问趋势折线图">
          <defs>
            <linearGradient id="revArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#22D3EE" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {gridFrac.map((f) => {
            const gy = padT + plotH - f * plotH;
            return (
              <g key={f}>
                <line x1={padL} x2={TW} y1={gy} y2={gy} stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray="2 3" />
                <text x={TW - 2} y={gy - 2} textAnchor="end" fontSize="7" fill="rgba(255,255,255,0.35)" fontFamily="monospace">
                  {Math.round(maxV * f)}
                </text>
              </g>
            );
          })}
          {line("visits", "#22D3EE")}
          {line("uniqueIps", "#A78BFA")}
          {days.map((d, i) =>
            i % 2 === 0 ? (
              <text key={i} x={px(i)} y={TH - 5} textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.5)" fontFamily="monospace">
                {d.date.slice(5)}
              </text>
            ) : null
          )}
        </svg>
      </div>
    );
  };

  // ===== 自动周报渲染 =====
  const renderReport = () => {
    if (reportBusy) {
      return (
        <div className="flex items-center justify-center gap-2 py-4 text-gray-500">
          <Loader2 size={13} className="animate-spin" />
          <span className="text-[10px] font-mono">AI CEO 正在聚合本周数据并生成周报…</span>
        </div>
      );
    }
    if (!report) return null;
    const exec = report.executive || {};
    const km = exec.keyMetrics || {};
    const recs: any[] = Array.isArray(report.recommendations) ? report.recommendations : [];
    const highRisks: any[] = Array.isArray(report.risk?.high) ? report.risk.high : [];
    const prColor: Record<string, string> = {
      P0: "#F87171", P1: "#FBBF24", P2: "#60A5FA", P3: "#9CA3AF",
    };
    return (
      <div className="space-y-2">
        {/* 头条 + 核心指标 */}
        <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={10} className="text-violet-400" />
            <span className="text-[10px] font-mono text-gray-300">{exec.headline || "AI CEO 周报"}</span>
          </div>
          <div className="text-[9px] font-mono text-gray-500 mb-2">
            周期 {report.date} 起 · 周期类型 {report.period} · {report.filePath || "已归档 Obsidian"}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {[
              { label: "收入(元)", value: fmtNum(km.revenue ?? 0), color: "#34D399" },
              { label: "新客户", value: fmtNum(km.newCustomers ?? 0), color: "#22D3EE" },
              { label: "发布内容", value: fmtNum(km.contentPublished ?? 0), color: "#A78BFA" },
              { label: "活跃工作流", value: fmtNum(km.activeWorkflows ?? 0), color: "#FBBF24" },
            ].map((k) => (
              <div key={k.label} className="px-2 py-1.5 rounded border border-white/[0.06] bg-white/[0.02]">
                <div className="text-[14px] font-bold leading-none" style={{ color: k.color }}>{k.value}</div>
                <div className="text-[8px] font-mono text-gray-500 mt-0.5">{k.label}</div>
              </div>
            ))}
          </div>
        </div>
        {/* 建议 + 风险 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Zap size={10} className="text-amber-400" />
              <span className="text-[10px] font-mono text-gray-300">可执行建议（{recs.length}）</span>
            </div>
            {recs.length === 0 ? (
              <div className="text-[9px] font-mono text-gray-600">暂无建议</div>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                {recs.slice(0, 6).map((r) => (
                  <div key={r.id || r.title} className="flex items-start gap-1.5 text-[9px] font-mono">
                    <span
                      className="px-1 py-0.5 rounded flex-shrink-0 text-[8px]"
                      style={{ color: prColor[r.priority] || "#9CA3AF", background: `${prColor[r.priority] || "#9CA3AF"}1a` }}
                    >
                      {r.priority || "P2"}
                    </span>
                    <span className="text-gray-300 leading-tight">{r.title}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <AlertTriangle size={10} className="text-rose-400" />
              <span className="text-[10px] font-mono text-gray-300">风险预警（高 {highRisks.length} / 共 {report.risk ? (report.risk.high?.length || 0) + (report.risk.medium?.length || 0) : 0}）</span>
            </div>
            {highRisks.length === 0 ? (
              <div className="text-[9px] font-mono text-emerald-400/80">✓ 无高风险项</div>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar">
                {highRisks.slice(0, 5).map((r, i) => (
                  <div key={i} className="text-[9px] font-mono text-rose-300 leading-tight">
                    · {r.description || r.category}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // 视图
  // ============================================
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] flex flex-col flex-shrink-0">
      {/* 头部 */}
      <div className="px-3 py-2 border-b border-white/[0.06] flex items-center gap-2 flex-shrink-0">
        <BarChart3 size={11} className="text-violet-400" />
        <span className="text-[10px] font-mono text-gray-300 uppercase tracking-wider">效果复盘看板</span>
        <span className="text-[9px] font-mono text-gray-600">趋势 · 聚合 · 自动周报</span>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="ml-auto flex items-center gap-1 px-2 py-1 rounded border border-white/[0.08] text-[10px] font-mono text-gray-400 hover:text-white hover:border-white/20 transition-colors"
        >
          <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
          刷新
        </button>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center py-10 text-gray-500 gap-2">
          <Loader2 size={14} className="animate-spin" />
          <span className="text-[11px] font-mono">正在拉取复盘数据…</span>
        </div>
      ) : error ? (
        <div className="px-3 py-6 text-center text-[11px] font-mono text-amber-400/90">{error}</div>
      ) : data ? (
        <div className="p-3 space-y-2">
          {/* A. 聚合统计 */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <StatCard label="今日访问" value={data.todayVisits} color="#22D3EE" icon={Globe} />
            <StatCard label="累计访问" value={fmtNum(data.totalVisits)} color="#A78BFA" icon={Users} />
            <StatCard label="GEO 访客" value={data.geoTotalVisitors} color="#34D399" icon={Target} />
            <StatCard label="平均停留" value={fmtDur(data.avgDuration)} color="#FBBF24" icon={Clock} />
            <StatCard label="跳出率" value={`${data.bounceRate}%`} color="#F472B6" icon={TrendingUp} />
            <StatCard label="资料下载" value={data.downloadCount} color="#60A5FA" icon={FileDown} />
          </div>

          {/* B. 效果趋势（近 14 天折线图） */}
          <div className="rounded-lg border border-white/[0.05] bg-black/20 p-3">
            <div className="flex items-center gap-1.5 mb-1">
              <TrendingUp size={10} className="text-cyan-400" />
              <span className="text-[10px] font-mono text-gray-300">近 14 天访问趋势（折线）</span>
            </div>
            {renderTrend()}
          </div>

          {/* C. 自动周报 */}
          <div className="rounded-lg border border-violet-500/15 bg-violet-500/[0.03] p-3">
            <div className="flex items-center gap-1.5 mb-2 flex-wrap">
              <FileText size={10} className="text-violet-400" />
              <span className="text-[10px] font-mono text-gray-300">AI CEO 自动周报</span>
              <button
                onClick={generateReport}
                disabled={reportBusy}
                className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded border border-violet-400/40 text-[10px] font-mono text-violet-300 hover:bg-violet-400/10 transition-colors disabled:opacity-50"
              >
                {reportBusy ? (
                  <Loader2 size={10} className="animate-spin" />
                ) : (
                  <Sparkles size={10} />
                )}
                {reportBusy ? "生成中…" : "生成自动周报"}
              </button>
            </div>
            <div className="text-[9px] font-mono text-gray-500 mb-2">
              聚合本周官网访问 / 内容 / 工作流 / 客户数据，AI CEO 生成头条、核心指标、可执行建议与风险预警，并自动归档到 Obsidian「Daily」目录。
            </div>
            {reportMsg && (
              <div className={`mb-2 text-[10px] font-mono ${reportMsg.ok ? "text-emerald-300" : "text-rose-300"}`}>
                {reportMsg.ok ? <CheckCircle2 size={11} className="inline mr-1" /> : <AlertTriangle size={11} className="inline mr-1" />}
                {reportMsg.text}
              </div>
            )}
            {renderReport()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
